import React, { useContext, useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import PageWrapper from "../../../components/PageWrapper";
import useLanguage from "../../../utils/useLanguage";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import searchIcon from "../../../assets/search_icon.png";
import printIcon from "../../../assets/printer_icon.png";
import previousIcon from "../../../assets/previous_icon.png";
import nextIcon from "../../../assets/next_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import SectionDivider from "../../../components/SectionDivider";
import { UserAuth } from "../../../utils/AuthContext";
import BackToDashboard from "../../../components/BackToDashboard";
import Loader from "../../../components/Loader";
import AreYouSureModal from "../../../components/AreYouSureModal";
import PageHeadline from "../../../components/PageHeadline";
import ProductOrderList from "./ProductOrderList";
import SelectedProductOrderList from "./SelectedProductOrderList";
import { getPaymentCode } from "../../../services/PaymentCodeService";
import {
  createProductOrder,
  getAllProducts,
} from "../../../services/productServices";
import { formatDate } from "../../../utils/toDate";
import InformationBox from "../../../components/InformationBox";
import PrintableProductOrderView from "./PrintableProductOrder";
import ErrorBox from "../../../components/ErrorBox";
import ErrorMessage from "../../../components/ErrorMessage";

const AddBookOrder = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const printRef = useRef(null);

  // STATES
  const [selectedBooks, setSelectedBooks] = useState({});
  const [allSelectedBooks, setAllSelectedBooks] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    orderNumber: "",
    date: new Date(),
    companyName: "",
    createdBy: "",
    notes: "",
  });
  const [showAreYouSure, setAreYouSure] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState("");

  const [loadingBooks, setLoadingBooks] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // Updated states for frontend search
  const [allBooksData, setAllBooksData] = useState([]); // Store all fetched books
  const [booksData, setBooksData] = useState([]); // Filtered books for display
  const [orderData, setOrderData] = useState({ orderNumber: null, date: null });
  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "usd",
    value: "USD",
  });

  // Order creation states
  const [confirmOrderLoading, setConfirmOrderLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState(null);

  // Book headers configuration
  const bookHeaders = [
    { key: "checkbox", label: "choose", type: "checkbox" },
    { key: "name", label: "bookName", type: "text" },
    { key: "productSubType", label: "bookType", type: "text" },
    { key: "academicLevel", label: "academicLevel", type: "text" },
    { key: "quantity", label: "quantity", type: "input", unit: "book" },
    // { key: "price", label: "price", type: "input", unit: "currency" },
  ];

  // Function to get order number
  const getOrderNumber = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status == 200) {
        setReceiptNumber(response?.data?.code);
        setOrderData((prev) => ({
          ...prev,
          orderNumber: response?.data?.code,
        }));
        const isUser = userData?.userType === "orgUser";
        setOrderDetails((prev) => ({
          ...prev,
          orderNumber: response?.data?.code,
          companyName: isUser ? userData.organizationName : "",
          createdBy: isUser ? userData.username : "",
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Get all books from API (modified to fetch all books at once)
  const getAllBooks = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingBooks(true);

      const response = await getAllProducts({
        page: 1,
        limit: 1000, // Fetch all books at once
        organizationId: organizationId,
        productType: "book",
      });

      if (response.data?.products) {
        // Group books by academic level
        const groupedBooks = groupBooksByAcademicLevel(response.data.products);
        setAllBooksData(groupedBooks); // Store all books
        setBooksData(groupedBooks); // Initially display all books
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        });
      }
    } catch (err) {
      console.error("Failed to fetch books:", err);
    } finally {
      setLoadingBooks(false);
    }
  };

  // Frontend search function
  const filterBooks = (query) => {
    if (!query.trim()) {
      setBooksData(allBooksData);
      return;
    }

    const filtered = allBooksData
      .map((category) => {
        const filteredBooks = category.books.filter((book) =>
          book.name.toLowerCase().includes(query.toLowerCase())
        );

        // Only return category if it has matching books
        if (filteredBooks.length > 0) {
          return {
            ...category,
            books: filteredBooks,
          };
        }
        return null;
      })
      .filter(Boolean); // Remove null categories

    setBooksData(filtered);
  };

  // Helper function to group books by academic level
  const groupBooksByAcademicLevel = (books) => {
    const grouped = books.reduce((acc, book) => {
      // Group by class ID but store the class name for display
      const groupId = book.schoolClassId || "ungrouped";
      const groupName = book.schoolClass?.name || groupId;

      if (!acc[groupId]) {
        acc[groupId] = {
          id: groupId,
          category: groupName, // Use class name for display
          books: [],
        };
      }
      acc[groupId].books.push({
        ...book,
        quantity: "",
        price: "",
        totalPrice: "",
        academicLevel: groupName,
        productSubType: t(book.productSubType),
      });
      return acc;
    }, {});

    return Object.values(grouped);
  };

  /* HANDLER FUNCTIONS */
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
    @page { 
      size: A4; 
      margin: 5mm;
      width: 21cm;
      height: 29.7cm;
    }
    @media print {
      body { 
        -webkit-print-color-adjust: exact;
        padding: 10mm;
      }
      .printable-order-view { display: flex !important; }
    }
  `,
  });

  // Validation function to check if all selected books have quantities
  const validateSelectedBooks = () => {
    const errors = [];

    allSelectedBooks.forEach((category) => {
      category.books.forEach((book) => {
        const quantity = parseInt(book.quantity) || 0;
        if (quantity <= 0) {
          errors.push(`${book.name} (${category.category})`);
        }
      });
    });

    return errors;
  };

  // Check if order can be confirmed
  const canConfirmOrder = () => {
    if (allSelectedBooks.length === 0) return false;

    return allSelectedBooks.every((category) =>
      category.books.every((book) => {
        const quantity = parseInt(book.quantity) || 0;
        return quantity > 0;
      })
    );
  };

  const handleConfirmOrder = () => {
    const validationErrors = validateSelectedBooks();

    if (validationErrors.length > 0) {
      const errorMessage =
        t("pleaseProvideQuantityFor") + ":\n" + validationErrors.join("\n");
      alert(errorMessage);
      return;
    }

    setAreYouSure(true);
  };

  // Order creation function
  const handleCreateOrder = async () => {
    setConfirmOrderLoading(true);
    setOrderError(null);

    try {
      // Prepare the order data according to the endpoint requirements
      const isUser = userData?.userType === "orgUser";
      const orderData = {
        orderNumber: orderDetails.orderNumber,
        type: "book",
        date: orderDetails.date || new Date().toISOString(),
        notes: orderDetails.notes,
        orders: allSelectedBooks
          .map((category) =>
            category.books.map((book) => ({
              productId: book.id,
              requestedQuantity: parseInt(book.quantity) || 0,
            }))
          )
          .flat(),
        userId: isUser ? parseInt(userData.userId) : null,
      };

      const response = await createProductOrder(orderData);

      if (response.status === 201) {
        setOrderSuccess(true);
        setSelectedBooks([]);
        setAreYouSure(false);
        return response.data;
      } else {
        throw new Error(response.data?.error || "Failed to create order");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      setOrderError(
        t("errorCreatingBookOrder")
      );
      throw error;
    } finally {
      setConfirmOrderLoading(false);
    }
  };

  const handlePrintAndSave = async () => {
    try {
      await handleCreateOrder();
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  // Book selection handler
  const handleBookSelection = (categoryId, bookId, isSelected) => {
    setSelectedBooks((prev) => {
      if (bookId === null) {
        // Remove entire category
        const { [categoryId]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [bookId]: isSelected,
        },
      };
    });

    if (bookId === null) {
      // Remove entire category
      setAllSelectedBooks((prev) =>
        prev.filter((item) => item.category !== categoryId)
      );
    } else {
      if (isSelected) {
        // Add book to selected list
        const category = allBooksData.find(
          (cat) => cat.category === categoryId
        ); // Use allBooksData to ensure we can find the book
        if (category) {
          const book = category.books.find((b) => b.id === bookId);
          if (book) {
            setAllSelectedBooks((prev) => {
              const categoryIndex = prev.findIndex(
                (cat) => cat.category === categoryId
              );

              if (categoryIndex >= 0) {
                // Category exists, add book to it
                const bookExists = prev[categoryIndex].books.some(
                  (b) => b.id === bookId
                );
                if (!bookExists) {
                  const updatedCategories = [...prev];
                  updatedCategories[categoryIndex] = {
                    ...updatedCategories[categoryIndex],
                    books: [
                      ...updatedCategories[categoryIndex].books,
                      { ...book },
                    ],
                  };
                  return updatedCategories;
                }
                return prev;
              } else {
                // New category
                return [
                  ...prev,
                  {
                    id: category.id,
                    category: categoryId,
                    books: [{ ...book }],
                  },
                ];
              }
            });
          }
        }
      } else {
        // Remove book from selected list
        setAllSelectedBooks((prev) => {
          const updatedCategories = prev.map((cat) => {
            if (cat.category === categoryId) {
              return {
                ...cat,
                books: cat.books.filter((book) => book.id !== bookId),
              };
            }
            return cat;
          });

          // Remove categories with no books
          return updatedCategories.filter((cat) => cat.books.length > 0);
        });
      }
    }
  };

  // Update input values for the selected books
  const handleInputChange = (categoryId, bookId, field, value) => {
    setAllSelectedBooks((prev) => {
      return prev.map((category) => {
        if (category.category === categoryId) {
          return {
            ...category,
            books: category.books.map((book) => {
              if (book.id === bookId) {
                const updatedBook = {
                  ...book,
                  [field]: value || 0,
                };

                // Auto-calculate total price when quantity changes
                if (field === "quantity") {
                  updatedBook.totalPrice =
                    (updatedBook.price || 0) * (value || 0);
                }

                return updatedBook;
              }
              return book;
            }),
          };
        }
        return category;
      });
    });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterBooks(query);
  };

  // Handle search button click
  const handleSearchClick = () => {
    filterBooks(searchQuery);
  };

  // Reset data after successful order
  const resetToOriginalData = () => {
    setBooksData(allBooksData);
    setSearchQuery("");
    setOrderData({ orderNumber: null, date: null });
    setAllSelectedBooks([]);
    getOrderNumber();
  };

  /* USEEFECTS */
  useEffect(() => {
    getAllBooks();
  }, []); // Remove dependencies since we're fetching all data once

  useEffect(() => {
    getOrderNumber();
  }, []);

  useEffect(() => {
    if (orderSuccess) {
      handlePrint();
      resetToOriginalData(); // Reset to original data instead of refetching
      setOrderSuccess(false);
    }
  }, [orderSuccess]);

  return (
    <PageWrapper withBackButton title={t("addBookOrder")}>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_700">
        <div className="o-column-row-switcher-reversed o-column--horizontal-align_space-between o-row--gap-size_300">
          <div className="c-search-bar">
            <section className="c-search-bar-label-input">
              <CustomInput
                placeholder={t("search")}
                value={searchQuery}
                onChange={handleSearchChange} // Updated to use new handler
                resetSearch={() => {
                  setSearchQuery("");
                  setBooksData(allBooksData); // Reset to show all books
                }}
                thick
              />
            </section>

            <CustomButton
              title={t("search")}
              iconSize={15}
              classList="c-search-button"
              icon={searchIcon}
              onClick={handleSearchClick} // Updated to use frontend search
            />
          </div>

          <div className="o-row o-row--gap-size_300">
            <CustomInput
              title={t("orderNumber") + ": "}
              value={orderData?.orderNumber || receiptNumber}
              flex={false}
              readonly
              loading={loadingBooks}
            />
            <CustomInput
              type="date"
              title={t("Date") + ": "}
              value={formatDate(orderDetails.date)}
              onChange={(e) => {
                setOrderDetails((prev) => ({
                  ...prev,
                  date: new Date(e.target.value),
                }));
              }}
              flex={false}
              loading={loadingBooks}
            />
          </div>
        </div>

        <SectionDivider title={t("theBooks")} />
        {loadingBooks && <Loader />}
        <ProductOrderList
          data={booksData}
          selectedItems={selectedBooks}
          onItemSelect={handleBookSelection}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          allSelectedItems={allSelectedBooks}
          headers={bookHeaders}
          groupByField="category"
          itemsField="books"
          itemKeyField="id"
          groupLabelPrefix="academicLevel"
        />
                {orderError && (
          <ErrorBox>{orderError}</ErrorBox>
        )}
        {allSelectedBooks.length > 0 && (
          <SectionDivider title={t("theAddedBooks")} />
        )}
        <SelectedProductOrderList
          data={allSelectedBooks}
          setSelectedItems={setAllSelectedBooks}
          currency={selectedCurrency}
          selectedItems={selectedBooks}
          onItemSelect={handleBookSelection}
          onInputChange={handleInputChange}
          headers={bookHeaders}
          groupByField="category"
          itemsField="books"
          itemKeyField="id"
          groupLabelPrefix="academicLevel"
          showDeleteButton={true}
          deleteButtonLabel="delete"
        />


        {allSelectedBooks.length > 0 && (
          <CustomInput
          title={`${t("notes")}: `}
          type="textarea"
          onChange={(e) => {
            setOrderDetails((prev) => ({ ...prev, notes: e.target.value }));
          }}
          />
        )}

        {orderError && (
          <ErrorMessage>{orderError}</ErrorMessage>
        )}
        {/* Validation message */}
        {allSelectedBooks.length > 0 && !canConfirmOrder() && (
          <InformationBox>
            {t("pleaseProvideQuantityForAllSelectedBooks")}
          </InformationBox>
        )}

        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          {allSelectedBooks.length > 0 && (
            <>
              <CustomButton
                title={t("confirm")}
                onClick={handleConfirmOrder}
                icon={saveIcon}
                iconSize={16}
                disabled={
                  confirmOrderLoading || isPrinting || !canConfirmOrder()
                }
                style={{
                  opacity: !canConfirmOrder() ? 0.6 : 1,
                  cursor: !canConfirmOrder() ? "not-allowed" : "pointer",
                }}
              />
            </>
          )}
        </div>
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setAreYouSure(false)}
          onConfirm={handlePrintAndSave}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm={true}
          confirmButtonVariant={null}
          loading={confirmOrderLoading}
          error={orderError}
          confirmIconType="whiteCheck"

        />
      )}

      {/* Printable Book Order View - You'll need to create this component */}
      <PrintableProductOrderView
        ref={printRef}
        data={allSelectedBooks}
        currency={selectedCurrency}
        orderDetails={orderDetails}
      />
    </PageWrapper>
  );
};

export default AddBookOrder;
